// frontend/src/components/portfolio/EditorHeader.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Visibility as PreviewIcon,
  Publish as PublishIcon,
  ArrowBack as BackIcon,
  Palette as PaletteIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { Portfolio, UpdatePortfolioRequest } from '../../types/Portfolio';
import { format } from 'date-fns';
import PreviewModal from './PreviewModal';
import PublishModal from './PublishModal';
import ThemeSettingsPanel from './ThemeSettingsPanel';
import SEOSettingsPanel from './SEOSettingsPanel';
import portfolioService from '../../services/portfolioService';

interface EditorHeaderProps {
  portfolio: Portfolio;
  hasUnsavedChanges: boolean;
  lastSaved: Date | null;
  saving: boolean;
  onSave: () => void;
  onUpdateMeta: (data: Partial<UpdatePortfolioRequest>) => void;
}

const EditorHeader: React.FC<EditorHeaderProps> = ({
  portfolio,
  hasUnsavedChanges,
  lastSaved,
  saving,
  onSave,
  onUpdateMeta
}) => {
  const navigate = useNavigate();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(portfolio.title);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [showThemeSettings, setShowThemeSettings] = useState(false);
  const [showSEOSettings, setShowSEOSettings] = useState(false);

  const handleTitleClick = () => {
    setIsEditingTitle(true);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitleValue(e.target.value);
  };

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    if (titleValue !== portfolio.title && titleValue.trim()) {
      onUpdateMeta({ title: titleValue });
    } else {
      setTitleValue(portfolio.title);
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleBlur();
    }
    if (e.key === 'Escape') {
      setTitleValue(portfolio.title);
      setIsEditingTitle(false);
    }
  };

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setShowExitDialog(true);
    } else {
      navigate('/admin/portfolios');
    }
  };

  const handleExitWithoutSaving = () => {
    setShowExitDialog(false);
    navigate('/admin/portfolios');
  };

  const handleSaveAndExit = async () => {
    await onSave();
    setShowExitDialog(false);
    navigate('/admin/portfolios');
  };

  const handlePublish = async (notes?: string) => {
    try {
      await portfolioService.publish(portfolio.id, notes);
      window.location.reload(); // Refresh to show published status
    } catch (error) {
      console.error('Failed to publish portfolio:', error);
      alert('Failed to publish portfolio. Please try again.');
    }
  };

  const handleThemeUpdate = (theme: {
    primaryColor: string;
    accentColor: string;
    fontFamily?: string;
    logo?: string;
  }) => {
    onUpdateMeta({ theme });
  };

  const handleSEOUpdate = (meta: {
    ogTitle?: string;
    ogDescription?: string;
    ogImage?: string;
    favicon?: string;
  }) => {
    onUpdateMeta({ meta });
  };

  const getSaveStatusText = () => {
    if (saving) {
      return 'Saving...';
    }
    if (hasUnsavedChanges) {
      return 'Unsaved changes';
    }
    if (lastSaved) {
      return `All changes saved at ${format(lastSaved, 'HH:mm')}`;
    }
    return 'No changes';
  };

  const getSaveStatusColor = () => {
    if (saving) return 'primary';
    if (hasUnsavedChanges) return 'warning';
    return 'success';
  };

  return (
    <>
      <Box
        sx={{
          height: 64,
          bgcolor: '#2C3E50',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,  // Increased from 3 to 4 (32px instead of 24px)
          boxShadow: 1,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          mb: 2  // Added 16px margin below header
        }}
      >
        {/* Left - Back Button & Portfolio Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
          <IconButton onClick={handleBackClick} size="small">
            <BackIcon />
          </IconButton>

          {isEditingTitle ? (
            <TextField
              value={titleValue}
              onChange={handleTitleChange}
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
              autoFocus
              size="small"
              variant="standard"
              sx={{ minWidth: 200 }}
              InputProps={{
                sx: {
                  fontSize: '1.25rem',
                  fontWeight: 600
                }
              }}
            />
          ) : (
            <Typography
              variant="h6"
              onClick={handleTitleClick}
              sx={{
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              {portfolio.title}
            </Typography>
          )}
        </Box>

        {/* Center - Save Status */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            minWidth: 200,  // Give it fixed minimum width
            justifyContent: 'center',  // Center the content within
            px: 3  // Add horizontal padding for breathing room
          }}
        >
          {saving && <CircularProgress size={16} />}
          <Typography
            variant="body2"
            sx={{
              color: `${getSaveStatusColor()}.main`,
              fontSize: '0.875rem',
              whiteSpace: 'nowrap'  // Prevent text wrapping
            }}
          >
            {getSaveStatusText()}
          </Typography>
        </Box>

{/* Right - Action Buttons */}
<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, justifyContent: 'flex-end' }}>
  <Button
    startIcon={<PaletteIcon />}
    onClick={() => setShowThemeSettings(true)}
    variant="outlined"
    size="small"
    sx={{ whiteSpace: 'nowrap' }}
  >
    Theme
  </Button>
  <Button
    startIcon={<LanguageIcon />}
    onClick={() => setShowSEOSettings(true)}
    variant="outlined"
    size="small"
    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', whiteSpace: 'nowrap' }}
  >
    SEO
  </Button>

  {/* Visual separator */}
  <Box sx={{ width: '1px', height: 24, bgcolor: 'rgba(255,255,255,0.2)', mx: 0.5 }} />

  <Button
    startIcon={<SaveIcon />}
    onClick={onSave}
    variant="outlined"
    size="small"
    disabled={!hasUnsavedChanges || saving}
    sx={{ whiteSpace: 'nowrap' }}
  >
    Save Now
  </Button>

  <Button
    startIcon={<PreviewIcon />}
    onClick={() => setShowPreview(true)}
    variant="outlined"
    size="small"
    sx={{ whiteSpace: 'nowrap' }}
  >
    Preview
  </Button>

  <Button
    startIcon={<PublishIcon />}
    onClick={() => setShowPublish(true)}
    variant="contained"
    size="small"
    disabled={portfolio.status === 'published'}
    sx={{ whiteSpace: 'nowrap' }}
  >
    {portfolio.status === 'published' ? 'Published' : 'Publish'}
  </Button>
</Box>  
      </Box>

      {/* Exit Warning Dialog */}
      <Dialog open={showExitDialog} onClose={() => setShowExitDialog(false)}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Do you want to save before leaving?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExitDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleExitWithoutSaving} color="error">
            Leave Without Saving
          </Button>
          <Button onClick={handleSaveAndExit} variant="contained">
            Save and Leave
          </Button>
        </DialogActions>
      </Dialog>

      {/* Theme Settings Modal */}
      <ThemeSettingsPanel
        open={showThemeSettings}
        onClose={() => setShowThemeSettings(false)}
        theme={portfolio.theme || { primaryColor: '#000000', accentColor: '#FF0000' }}
        onUpdate={handleThemeUpdate}
      />

      {/* SEO Settings Modal */}
      <SEOSettingsPanel
        open={showSEOSettings}
        onClose={() => setShowSEOSettings(false)}
        portfolio={{
          title: portfolio.title,
          description: portfolio.description,
          meta: portfolio.meta || {}
        }}
        onUpdate={handleSEOUpdate}
      />

      {/* Preview Modal */}
      <PreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        portfolio={portfolio}
      />

      {/* Publish Modal */}
      <PublishModal
        open={showPublish}
        onClose={() => setShowPublish(false)}
        onConfirm={handlePublish}
        portfolioTitle={portfolio.title}
        slug={portfolio.slug}
      />
    </>
  );
};

export default EditorHeader;